import React, { useContext ,Suspense} from 'react';
import LazyLoad from 'react-lazy-load';
import PropTypes from 'prop-types';
import Head from 'next/head'
import ImageFactory from '../../adapters/cloudinary/ImageFactory';
import { ICloudinaryImage } from '../../types';
import { ImageConstants, ImageContenfullConstants, ComponentFactoryConstants} from '../../adapters/helpers/Constants';
import Image from 'next/image';
import Loader from '../../adapters/helpers/Loader';


const Visibility = React.createContext(false);

export default function Backdrop(props) {
    const content = (
        <div {...props} className={`${props.className || ''} ob-backdrop`} />
    );
    
    // Check if this is a priority/LCP image that should not be lazy loaded
    const shouldSkipLazyLoad = props.priority || props.isLCP;
    
    return (
        <Suspense fallback={<Loader />}>
        {shouldSkipLazyLoad ? (
            <Visibility.Provider value={true}>
                {content}
            </Visibility.Provider>
        ) : (
            <LazyLoad offset={500} placeholder={
                <Visibility.Provider value={false}>
                    {content}
                </Visibility.Provider>
            }>
                <Visibility.Provider value={true}>
                    {content}
                </Visibility.Provider>
            </LazyLoad>
        )}
        </Suspense>
    );
}

Backdrop.propTypes = {
    className: PropTypes.string,
    style: PropTypes.object,
    priority: PropTypes.bool,
    isLCP: PropTypes.bool,
};

Backdrop.Image = BackdropImage;
function BackdropImage({
    preserveAspectRatio = true,
    className = '',
    image, xlg = image, lg = xlg, md = lg, sm = md,
    mobileImage = sm, xs = mobileImage, tiny = xs, xtiny = tiny,
    priority = false, isLCP = false,
}) {
    const visible = useContext(Visibility);
    const ispreload = mobileImage?.fields?.isPreload || priority || isLCP
    const isBannerImage = mobileImage?.fields?.isBannerImage || priority || isLCP
    const fallback = image || xlg || lg || md || sm || xs || tiny || xtiny;
    const contentImageId = mobileImage?.fields?.asset?.fields?.file?.url;
    const imageLoading = ispreload && isBannerImage ? "eager" : "lazy";
    
    // Determine if this should be treated as LCP image
    const shouldPrioritize = priority || isLCP || ispreload;
    return (
        <div className={`${className} ob-backdrop__image`}>
            {ispreload && isBannerImage && <Head>
                  <link rel='preload' href='/static/fonts/HurmeGeometricSans1-Bold.woff' as='font' crossOrigin='anonymous' fetchPriority='high' />
                  <link rel='preload' href='/static/fonts/HurmeGeometricSans1-Light.woff' as='font' crossOrigin='anonymous' fetchPriority='high'/>
                { contentImageId && <link rel="preload" href={ImageFactory.buildContentfullImageUrlByWidth(contentImageId, ImageConstants.lcpDesktopWidth)} as="image" media={`(min-width:${ImageConstants.largeDesktopMinWidth})`} fetchpriority="high" /> }
            </Head> }
            {!!preserveAspectRatio && (<>
                {!!xlg      && renderAspectRatio(xlg, 'xlg')}
                {!!lg       && renderAspectRatio(lg, 'lg')}
                {!!md       && renderAspectRatio(md, 'md')}
                {!!sm       && renderAspectRatio(sm, 'sm')}
                {!!xs       && renderAspectRatio(xs, 'xs')}
                {!!tiny     && renderAspectRatio(tiny, 'tiny')}
                {!!xtiny    && renderAspectRatio(xtiny, 'xtiny')}
            </>)}

            <picture className="ob-backdrop__pic">
                {!!xlg   && <source srcSet={ImageFactory.imageContentfullDpr(xlg?.fields?.asset?.fields?.file?.url, ImageContenfullConstants.dpr_1440)} media={ComponentFactoryConstants.mediaUpper} fetchpriority="high" />}
                {!!lg    && <source srcSet={ImageFactory.imageContentfullDpr(lg?.fields?.asset?.fields?.file?.url,  ImageContenfullConstants.dpr_1200)} media={ImageContenfullConstants.media_1200} fetchpriority="high" />}
                {!!md    && <source srcSet={ImageFactory.imageContentfullDpr(md?.fields?.asset?.fields?.file?.url,  ImageContenfullConstants.dpr_992)} media={ImageContenfullConstants.media_992}  fetchpriority="high" />}
                {!!sm    && <source srcSet={ImageFactory.imageContentfullDpr(sm?.fields?.asset?.fields?.file?.url,  ImageContenfullConstants.dpr_768)} media={ImageContenfullConstants.media_768}  fetchpriority="high" />}
                {!!xs    && <source srcSet={ImageFactory.imageContentfullDpr(xs?.fields?.asset?.fields?.file?.url,  ImageContenfullConstants.dpr_426)} media={ComponentFactoryConstants.mediaUpper}  fetchpriority="high" />}
                {!!tiny  && <source srcSet={ImageFactory.imageContentfullDpr(tiny?.fields?.asset?.fields?.file?.url,  ImageContenfullConstants.dpr_376)} media={ImageContenfullConstants.media_376}  fetchpriority="high" />}
                {!!xtiny && <source srcSet={ImageFactory.imageContentfullDpr(xtiny?.fields?.asset?.fields?.file?.url, ImageContenfullConstants.dpr_365)} fetchpriority="high" />}                 

                {(visible && fallback) ? (
                    <Image
                        className="ob-backdrop__img"
                        alt={fallback?.fields?.alternateText || ''}
                        src={ImageFactory.imageContentfullSrc(fallback?.fields?.asset?.fields?.file?.url, ImageContenfullConstants.dpr_1440)}
                        srcSet={ImageFactory.imageContentfullDpr(fallback?.fields?.asset?.fields?.file?.url, ImageContenfullConstants.dpr_1440)}
                        height={0}
                        width={0}
                        fetchPriority={ispreload && isBannerImage ? "high" : undefined}
                        sizes="(max-width: 768px) 100vw, 70vw"
                        style={{ width: '100%', height: 'auto' }}
                        loading={imageLoading}
                    />
                ) : (shouldPrioritize && fallback) && <Image
                loading={imageLoading}
                className="ob-backdrop__img"
                alt={fallback?.fields?.alternateText || ''}
                src={ImageFactory.imageContentfullSrc(fallback?.fields?.asset?.fields?.file?.url, ImageContenfullConstants.dpr_1440)}
                srcSet={ImageFactory.imageContentfullDpr(fallback?.fields?.asset?.fields?.file?.url, ImageContenfullConstants.dpr_1440)}
                height={0}
                width={0}
                fetchPriority={ispreload && isBannerImage ? "high" : undefined}
                sizes="(max-width: 768px) 100vw, 70vw"
                style={{ width: '100%', height: 'auto' }}
            />}
            </picture>
        </div>
    );
}

BackdropImage.propTypes = {
    className: PropTypes.string,
    alt: PropTypes.string,
    image: ICloudinaryImage,
    mobileImage: ICloudinaryImage,
    xtiny: ICloudinaryImage,
    tiny: ICloudinaryImage,
    xs: ICloudinaryImage,
    sm: ICloudinaryImage,
    md: ICloudinaryImage,
    lg: ICloudinaryImage,
    xlg: ICloudinaryImage,
    preserveAspectRatio: PropTypes.bool,
    priority: PropTypes.bool,
    isLCP: PropTypes.bool,
};

Backdrop.Content = BackdropContent;
function BackdropContent({ className, children = null}) {
    return (
        <div className={`${className || ''} ob-backdrop__content`}>
            {children}
        </div>
    );
}

BackdropContent.propTypes = {
    className: PropTypes.string,
    children: PropTypes.node,
};

function renderAspectRatio(image, breakpointKey) {
    const aspectRatio = image?.fields?.imageRendition?.fields?.aspectRatio;

    if (!aspectRatio) {
        return null;
    }

    return (
        <div
            className={`ob-backdrop__ratio ob-backdrop__ratio--${breakpointKey}`}
            style={{ paddingTop: 100 / aspectRatio + '%' }}
        />
    );
}
